import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Skeleton, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitDto, ReviewChangesDto, WaitingChangeDto, WaitingItemDto } from "../../../../domain/DataKitDto";
import "./DataKitReviewChanges.scss";

interface Props {
  kit: DataKitDto;
  canWrite: boolean;
  /** Edit opens the item drawer - the page owns it. */
  onEditItem: (itemId: string) => void;
  /** After a discard: the banner, the items and their counts all change. */
  onChanged: () => void;
  /** Changes whenever the page reloads its publish state - the list reloads with it. */
  refreshToken?: unknown;
}

/** "Today, 3:42 PM", "Oct 9, 3:42 PM" - when a change was saved. */
function whenLabel(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const time = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return `Today, ${time}`;
  return `${date.toLocaleDateString([], { month: "short", day: "numeric" })}, ${time}`;
}

/** The row's newest change, which is the one the who/when/where columns describe. */
function latest(row: WaitingItemDto): WaitingChangeDto | undefined {
  return [...row.changes].sort(
    (a, b) => new Date(b.editedDate || 0).getTime() - new Date(a.editedDate || 0).getTime()
  )[0];
}

/** Library page / Timber / Import - the "where" chip's colour. */
function sourceClass(source?: string | null): string {
  if (source === "TIMBER") return "timber";
  if (source === "IMPORT") return "import";
  return "library";
}

/**
 * Review changes (Oct 12): every change waiting to publish, opened from the banner as a section right
 * under it. Each item lists its changed fields - old → new, or "Removed" for a menu or location price
 * taken away - with who made the change, when, and where (the library page, Timber, or an import).
 * Discard puts that one item back to its published values; everything else stays waiting. Changes
 * saved before this shipped have no who/when/where - nothing recorded them.
 */
export function DataKitReviewChanges({ kit, canWrite, onEditItem, onChanged, refreshToken }: Props) {
  const [review, setReview] = useState<ReviewChangesDto | null>(null);
  const [query, setQuery] = useState("");
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!kit.id) return;
    DataKitApi.reviewChanges(kit.id)
      .then((res) => setReview(res.data))
      .catch(() => message.error("Could not load the changes waiting to publish"));
  }, [kit.id]);

  // Again whenever the page's publish state reloads - an item saved in the drawer, or a publish.
  useEffect(() => {
    load();
  }, [load, refreshToken]);

  const rows = useMemo(() => {
    const items = review?.items || [];
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((row) =>
      [
        row.name,
        ...row.groupPaths,
        ...row.changes.flatMap((c) => [
          c.label,
          c.oldValue,
          c.newValue,
          c.editedByName,
          c.sourceLabel,
          c.originDesignName,
        ]),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [review, query]);

  const discard = (row: WaitingItemDto) => {
    if (!kit.id) return;
    const key = row.itemId || row.groupId || row.name;
    const request =
      row.kind === "GROUP" && row.groupId
        ? DataKitApi.discardGroupChanges(kit.id, row.groupId)
        : row.itemId
          ? DataKitApi.discardItemChanges(kit.id, row.itemId)
          : null;
    if (!request) return;
    setBusyKey(key);
    request
      .then((res) => {
        setReview(res.data);
        message.success(`${row.name || "Item"} is back to its published version`);
        onChanged();
      })
      .catch(() => message.error("Could not discard these changes"))
      .finally(() => setBusyKey(null));
  };

  const count = review?.items.length ?? 0;

  return (
    <section className="data-kit-review">
      <div className="review-head">
        <strong>Changes waiting to publish</strong>
        <span className="review-count">
          {count} item{count === 1 ? "" : "s"}
        </span>
        <Input
          className="review-search"
          placeholder="Search changes"
          prefix={<SearchOutlined />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          allowClear
          aria-label="Search changes"
        />
      </div>

      {!review ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : (
        <div className="review-scroll">
          <table className="review-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>What changed</th>
                <th>Changed by</th>
                <th>Where</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="review-empty">
                    {count === 0 ? "Nothing is waiting to publish." : "No changes match."}
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const last = latest(row);
                  const key = row.itemId || row.groupId || row.name;
                  return (
                    <tr key={`${row.kind}:${key}`}>
                      <td className="review-name">
                        {row.name}
                        {row.groupPaths.length > 0 && (
                          <span className="review-path">
                            {row.kind === "GROUP" ? "Group" : row.groupPaths.join(" · ")}
                          </span>
                        )}
                      </td>
                      <td className="review-changes">
                        {row.changes.map((c) => (
                          <div className="review-change" key={c.changeKey || c.label}>
                            <span className="review-label">{c.label}</span>
                            {c.oldValue ? <s>{c.oldValue}</s> : null}
                            {c.oldValue ? " → " : null}
                            <b>{c.removed ? "Removed" : c.newValue || "None"}</b>
                          </div>
                        ))}
                      </td>
                      <td className="review-who">
                        {last?.editedByName || "—"}
                        {last?.editedDate && <span className="review-when">{whenLabel(last.editedDate)}</span>}
                      </td>
                      <td>
                        {last?.sourceLabel ? (
                          <span className={`review-source ${sourceClass(last.source)}`}>
                            {last.sourceLabel}
                            {last.source === "TIMBER" && last.originDesignName ? ` · ${last.originDesignName}` : ""}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="review-actions">
                        {canWrite && row.kind !== "GROUP" && row.itemId && (
                          <Button size="small" onClick={() => onEditItem(row.itemId as string)}>
                            Edit
                          </Button>
                        )}
                        {canWrite && (
                          <Button size="small" danger loading={busyKey === key} onClick={() => discard(row)}>
                            Discard
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
